import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    RichEditor,
    tiptapDocToPlainText,
    docFromPlainText,
    collectDocImageFileIds,
    applyResolvedUrlsToDoc
} from '@odo/shared-ui';
import type { TiptapDoc } from '@odo/shared-ui';
import type { PatientNote } from '../../../services/patient/notes';
import {
    resolveInternalImageUrls,
    uploadInternalImage
} from '../../../services/patient/internalImages';
import { MAX_NOTE_BODY_LENGTH, MAX_NOTE_TITLE_LENGTH } from './noteAccess';

const EMPTY_DOC: TiptapDoc = { type: 'doc', content: [] };

function initialDoc(note: PatientNote | null): TiptapDoc {
    if (note?.bodyDoc) {
        return note.bodyDoc;
    }
    if (note?.body) {
        return docFromPlainText(note.body);
    }
    return EMPTY_DOC;
}

export interface NoteModalSaveData {
    title: string;
    bodyDoc: TiptapDoc;
}

interface NoteModalProps {
    patientId: string;
    note: PatientNote | null;
    isSaving: boolean;
    onSave: (data: NoteModalSaveData) => void;
    onClose: () => void;
}

function NoteModal({ patientId, note, isSaving, onSave, onClose }: NoteModalProps) {
    const [title, setTitle] = useState(note?.title ?? '');
    const [doc, setDoc] = useState<TiptapDoc>(() => initialDoc(note));
    const titleRef = useRef<HTMLInputElement>(null);

    const isEdit = !!note;
    const plainText = tiptapDocToPlainText(doc).trim();
    const overLimit = plainText.length > MAX_NOTE_BODY_LENGTH;
    const canSave = title.trim().length > 0 && plainText.length > 0 && !overLimit && !isSaving;

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    // When editing a note that already has images, swap the stored (expired)
    // file-id references for freshly-signed URLs so they render in the editor.
    useEffect(() => {
        if (!note?.bodyDoc) {
            return;
        }
        const fileIds = collectDocImageFileIds(note.bodyDoc);
        if (fileIds.length === 0) {
            return;
        }
        let cancelled = false;
        resolveInternalImageUrls(patientId, fileIds)
            .then((urls) => {
                if (!cancelled) {
                    setDoc((d) => applyResolvedUrlsToDoc(d, urls));
                }
            })
            .catch(() => {
                /* leave unresolved — images may render broken, but text is intact */
            });
        return () => {
            cancelled = true;
        };
    }, [note, patientId]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isSaving) onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isSaving, onClose]);

    const handleImageUpload = useCallback(
        async (file: File) => {
            const result = await uploadInternalImage(patientId, file, 'note');
            return { url: result.url, fileId: result.fileId };
        },
        [patientId]
    );

    const handleSave = () => {
        if (!canSave) return;
        onSave({ title: title.trim(), bodyDoc: doc });
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="note-modal-title"
            // Deliberately no backdrop-click close: a half-written note is too
            // easy to lose to a stray click outside the modal. Closing is
            // explicit — the X button, Cancel, or Escape.
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        >
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-[560px] flex flex-col overflow-hidden relative"
                style={{ maxHeight: '90vh' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 id="note-modal-title" className="text-lg font-bold text-gray-900">
                        {isEdit ? 'Edit note' : 'Add note'}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        aria-label="Close"
                        className="text-gray-400 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-4">
                    <div>
                        <label
                            htmlFor="note-title"
                            className="block text-xs font-semibold text-gray-700 mb-1.5"
                        >
                            Title
                        </label>
                        <input
                            id="note-title"
                            ref={titleRef}
                            type="text"
                            value={title}
                            maxLength={MAX_NOTE_TITLE_LENGTH}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Short, descriptive title…"
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-semibold text-gray-700">Note</span>
                            <span
                                className={`text-xs ${overLimit ? 'text-red-500' : 'text-gray-400'}`}
                            >
                                {plainText.length}/{MAX_NOTE_BODY_LENGTH}
                            </span>
                        </div>
                        <RichEditor
                            value={doc}
                            onChange={setDoc}
                            onImageUpload={handleImageUpload}
                            placeholder="Add context, instructions, or observations…"
                            minHeightPx={160}
                        />
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 px-5 py-3.5 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-semibold text-sm disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!canSave}
                        className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-gray-900 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? (
                            <>
                                <div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Saving…
                            </>
                        ) : isEdit ? (
                            'Save changes'
                        ) : (
                            'Add note'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export { NoteModal };
